import { isPlatformBrowser } from '@angular/common';
import { Component, DestroyRef, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { catchError, of } from 'rxjs';

import { FrontendCouponBadgeSummary } from '../../../core/api/api.models';
import { AssetUrlPipe } from '../../../core/api/asset-url.pipe';
import { SavingArenaApiService } from '../../../core/api/saving-arena-api.service';
import { SeoService } from '../../../core/seo/seo.service';
import { breadcrumbSchemaFor, breadcrumbTrail } from '../../../core/seo/breadcrumb-trail';
import { itemListSchema, webPageSchema } from '../../../core/seo/structured-data';
import { Breadcrumb } from '../../../shared/breadcrumb/breadcrumb';
import { DevFlag } from '../../../shared/dev-flag/dev-flag';
import { COUPON_COLLECTION_BY_SLUG } from '../coupon-collections';

interface HubCard {
  slug: string;
  title: string;
  description: string;
  label: string;
  color: string;
  iconUrl: string;
  isSystem: boolean;
  count: number;
}

/**
 * `/coupons` — the coupon-badges hub. Lists every active coupon badge (the four system badges plus
 * any admin-created custom ones) as a card linking to its `/coupons/<slug>` collection page. Data is
 * fetched live from `/coupon-badges`, so a new badge added in admin shows up here automatically.
 */
@Component({
  selector: 'app-coupons-hub-page',
  imports: [Breadcrumb, RouterLink, AssetUrlPipe, DevFlag],
  templateUrl: './coupons-hub-page.html',
  styleUrl: './coupons-hub-page.scss',
})
export class CouponsHubPage {
  protected readonly crumbs = breadcrumbTrail({ label: 'Coupons' });

  private readonly api = inject(SavingArenaApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly seo = inject(SeoService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  protected readonly cards = signal<HubCard[]>([]);

  /** Drives the skeleton grid — the page used to render an empty void until the badges arrived. */
  protected readonly loading = signal(true);
  /** Placeholder count for the skeleton grid; matches the four system badges. */
  protected readonly skeletons = [1, 2, 3, 4];
  /** Separates "the request failed" from "there genuinely are no badges" — the copy differs. */
  protected readonly failed = signal(false);

  /** Total live offers across every badge — the hero "Live offers" stat. */
  protected readonly totalOffers = computed(() =>
    this.cards().reduce((sum, card) => sum + card.count, 0),
  );

  constructor() {
    this.api
      .getCouponBadges()
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        const badges = response?.success ? (response.data ?? []) : [];
        this.failed.set(response === null);
        this.cards.set(badges.map((badge) => this.toCard(badge)));
        this.loading.set(false);
        this.applySeo();
      });
  }

  /**
   * System badges keep the polished storefront copy from the static collection defs; custom badges
   * fall back to their own name + admin description.
   */
  private toCard(badge: FrontendCouponBadgeSummary): HubCard {
    const staticDef = COUPON_COLLECTION_BY_SLUG[badge.slug];
    const name = badge.name?.trim() || 'Coupons';
    return {
      slug: badge.slug,
      title: staticDef ? staticDef.title : `${name} Coupons`,
      description: staticDef
        ? staticDef.description
        : badge.description?.trim() || `Browse coupons and deals tagged ${name}.`,
      label: badge.label?.trim() || name,
      color: badge.color || '#741FA2',
      iconUrl: badge.iconImagePath ? String(badge.iconImagePath) : '',
      isSystem: badge.isSystem,
      count: Number(badge.count) || 0,
    };
  }

  /** A tinted background for the icon box, derived from the badge colour. */
  protected iconTint(color: string): string {
    return /^#[0-9a-fA-F]{6}$/.test(color) ? `${color}1f` : color;
  }

  private applySeo(): void {
    if (!this.isBrowser && !this.cards().length) {
      // Still set base SEO on the server render even if the badge fetch is mid-flight.
    }
    const title = 'Coupons by Type: Verified, Exclusive, Free Shipping & Student | SavingArena';
    const description =
      'Browse coupons by type: verified working codes, SavingArena-exclusive deals, free shipping offers, student discounts and more.';

    this.seo.apply({ title, description, canonicalPath: '/coupons' });
    this.seo.setJsonLd([
      webPageSchema({
        origin: this.seo.origin,
        url: this.seo.absolute('/coupons'),
        name: title,
        description,
      }),
      breadcrumbSchemaFor(this.crumbs, (p) => this.seo.absolute(p)),
      itemListSchema(
        this.cards().map((card) => ({
          '@type': 'WebPage',
          name: card.title,
          url: this.seo.absolute(`/coupons/${card.slug}`),
        })),
        'Coupon collections',
      ),
    ]);
  }
}
